import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

const argonTheme = {
	COLORS: {
		DEFAULT: '#32325d',
		INFO: '#17a2b8',
		BG_SIMULATOR: '#f8f9fa',
		BORDER: '#dee2e6',
	},
};

const RadioInput = ({ data, handelChange, currentFormValue }) => {
	const [value, setValue] = useState(null);
	const [options, setOptions] = useState(data?.options || []);
	const [loading, setLoading] = useState(false);
	const [spinner, setSpinner] = useState(false);
	const handelChangeRef = useRef(handelChange);
	const currentFormValueRef = useRef(currentFormValue);
	useEffect(() => {
		handelChangeRef.current = handelChange;
		currentFormValueRef.current = currentFormValue;
	});

	const handleChange = (optionValue, option) => {
		setValue(optionValue);
		const selectedOption = options.find((opt) => opt.value === optionValue);
		if (handelChangeRef.current) {
			handelChangeRef.current(
				selectedOption?.nextStep || data?.nextStepData || false,
				option?.description,
			);
		}
	};

	useEffect(() => {
		if (data?.options?.length) {
			setOptions(data.options);
		}
	}, [data]);

	const calculateButtonSize = () => {
		if (typeof window === 'undefined') return { width: 200, height: 150 };

		const screenWidth = window.innerWidth;

		if (options.length <= 2) {
			return {
				width: Math.min(300, (screenWidth - 60) * 0.8),
				height: Math.min(150, (screenWidth - 60) * 0.6),
			};
		} else {
			const containerWidth = screenWidth - 60;
			const columns = Math.min(3, options.length);
			const size = Math.min(containerWidth / columns - 40, 150);
			return { width: size, height: size };
		}
	};

	const buttonSize = calculateButtonSize();

	return (
		<Card
			className='mb-4 border-0'
			style={{
				backgroundColor: argonTheme.COLORS.BG_SIMULATOR,
				borderRadius: '15px',
				padding: '20px',
			}}>
			{data?.label && (
				<Card.Title
					className='mb-4'
					style={{
						color: argonTheme.COLORS.DEFAULT,
						fontSize: '1.1rem',
						fontWeight: '600',
					}}>
					{data.label}
					{data.required && <span className='text-danger ms-1'>*</span>}
				</Card.Title>
			)}

			<Card.Body className='p-0'>
				{spinner ? (
					<div className='text-center py-5'>
						<div className='spinner-border text-info' role='status'>
							<span className='visually-hidden'>Loading...</span>
						</div>
					</div>
				) : options?.length > 0 ? (
					<Row className='g-3 justify-content-center'>
						{options.map((option, idx) => {
							const isSelected = value === option.value;
							const isDisabled = option.disabled;

							return (
								<Col
									key={idx}
									xs={6}
									sm={4}
									md={3}
									lg={2}
									className='d-flex justify-content-center'>
									<Button
										variant='light'
										className={`d-flex flex-column align-items-center justify-content-center p-3 ${
											isDisabled ? 'opacity-50' : ''
										}`}
										style={{
											width: '100%',
											height: buttonSize.height,
											minHeight: '120px',
											border: `2px solid ${
												isSelected
													? argonTheme.COLORS.INFO
													: argonTheme.COLORS.BORDER
											}`,
											borderRadius: '12px',
											backgroundColor: 'white',
										}}
										disabled={isDisabled}
										onClick={() =>
											!isDisabled && handleChange(option.value, option)
										}>
										{option.imageUrl ? (
											<div
												className='mb-2'
												style={{ width: '80%', height: '80%' }}>
												<img
													src={option.imageUrl}
													alt={option.label}
													className='img-fluid h-100 w-100'
													style={{ objectFit: 'contain' }}
												/>
											</div>
										) : option.color ? (
											<div
												className='mb-2'
												style={{
													width: '80%',
													height: '80%',
													backgroundColor: option.color,
													borderRadius: '6px',
												}}
											/>
										) : null}

										<span className='text-center mt-2 small'>
											{option.label}
										</span>
									</Button>
								</Col>
							);
						})}
					</Row>
				) : (
					<div className='text-center py-5'>
						<p className='text-muted mb-0'>Aucune option disponible</p>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

RadioInput.propTypes = {
	data: PropTypes.object.isRequired,
	handelChange: PropTypes.func.isRequired,
	currentFormValue: PropTypes.object,
};

export default RadioInput;
